import React, { useState } from 'react';
import api from '../utils/api';
import { X, MessageCircle, Copy, Link } from 'lucide-react';
import toast from 'react-hot-toast';

// Payment Link Modal for Leads (pre-enrollment)
export function LeadPaymentLinkModal({ lead, onClose }) {
  const [amount, setAmount] = useState(lead.monthlyFee || '');
  const [type, setType] = useState('upi');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!amount || Number(amount) <= 0) return toast.error('Amount enter karo');
    setLoading(true);
    try {
      const r = await api.post(`/payment-link/lead/${lead.id}`, { amount, type, description });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">💳 Payment Link — {lead.name}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {!result ? (
            <>
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input className="form-control" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Partial payment allowed — enter any amount</div>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className={`btn flex-1 ${type === 'upi' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setType('upi')} style={{ justifyContent: 'center' }}>
                    📱 UPI Link
                  </button>
                  <button className={`btn flex-1 ${type === 'razorpay' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setType('razorpay')} style={{ justifyContent: 'center' }}>
                    🔗 Razorpay
                  </button>
                </div>
                {type === 'upi' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, padding: '6px 10px', background: 'var(--surface)', borderRadius: 6 }}>
                    📱 Client ke phone pe PhonePe/GPay/Paytm mein directly open hoga
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <input className="form-control" value={description} onChange={e => setDescription(e.target.value)} placeholder="Easy Settlement — Analysis Fee" />
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', padding: '16px 0', marginBottom: 16 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>₹{Number(result.amount).toLocaleString('en-IN')}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Ref: {result.ref} · {result.linkType?.toUpperCase()}</div>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Link</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control" value={result.link} readOnly style={{ fontSize: 12 }} />
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(result.link)}><Copy size={13} /></button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp Message</label>
                <textarea className="form-control" rows={8} value={result.waMessage} readOnly style={{ fontSize: 12, lineHeight: 1.7 }} />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result ? (
            <button className="btn btn-primary" onClick={generate} disabled={loading}>
              {loading ? <span className="spinner" /> : '🔗 Generate Link'}
            </button>
          ) : (
            <button className="btn btn-whatsapp" onClick={() => { window.open(result.waLink, '_blank'); toast.success('WhatsApp opened!'); }}>
              <MessageCircle size={14} /> Send via WhatsApp
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Payment Link Modal for Enrolled Clients (retainer)
export function ClientPaymentLinkModal({ lead, onClose }) {
  const monthlyFee = lead.monthlyFee || 0;
  const [amount, setAmount] = useState(monthlyFee);
  const [type, setType] = useState('upi');
  const [partialReason, setPartialReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [needsApproval, setNeedsApproval] = useState(false);

  const fiftyPct = Math.round(monthlyFee * 0.5);
  const isPartial = Number(amount) < monthlyFee;
  const belowFifty = Number(amount) < fiftyPct;

  const generate = async () => {
    if (!amount || Number(amount) <= 0) return toast.error('Amount enter karo');
    if (belowFifty && !partialReason) return toast.error('50% se kam hai — reason batao');
    setLoading(true);
    try {
      const r = await api.post(`/payment-link/client/${lead.id}`, { amount, type, partialReason });
      if (r.data.needsApproval) {
        setNeedsApproval(true);
      } else {
        setResult(r.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  if (needsApproval) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 420 }}>
          <div className="modal-header">
            <div className="modal-title">⏳ Approval Required</div>
            <button className="btn-icon" onClick={onClose}><X size={16} /></button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>TL/Manager Approval Required</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              ₹{Number(amount).toLocaleString('en-IN')} is below 50% of monthly fee (₹{fiftyPct.toLocaleString('en-IN')}).
              Approval request sent to TL/Admin. You will be notified once approved.
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary w-full" style={{ justifyContent: 'center' }} onClick={onClose}>OK</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div className="modal-title">💳 Retainer Payment — {lead.name}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {!result ? (
            <>
              {/* Amount selector */}
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button className={`btn btn-sm ${Number(amount) === monthlyFee ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setAmount(monthlyFee)}>
                    Full — ₹{monthlyFee.toLocaleString('en-IN')}
                  </button>
                  <button className={`btn btn-sm ${Number(amount) === fiftyPct ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setAmount(fiftyPct)}>
                    50% — ₹{fiftyPct.toLocaleString('en-IN')}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center' }}>or custom:</span>
                </div>
                <input className="form-control" type="number" value={amount} onChange={e => setAmount(e.target.value)} />

                {/* Status indicator */}
                {isPartial && !belowFifty && (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
                    ⚠️ Partial payment (50%+) — allowed without approval
                  </div>
                )}
                {belowFifty && (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: '#fee2e2', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
                    🔴 Below 50% — TL/Manager approval required
                  </div>
                )}
              </div>

              {belowFifty && (
                <div className="form-group">
                  <label className="form-label">Reason for below 50% *</label>
                  <textarea className="form-control" rows={2} value={partialReason} onChange={e => setPartialReason(e.target.value)} placeholder="Client ka reason batao..." />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className={`btn flex-1 ${type === 'upi' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setType('upi')} style={{ justifyContent: 'center' }}>📱 UPI</button>
                  <button className={`btn flex-1 ${type === 'razorpay' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setType('razorpay')} style={{ justifyContent: 'center' }}>🔗 Razorpay</button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', padding: '16px 0', marginBottom: 16 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>₹{Number(result.amount).toLocaleString('en-IN')}</div>
                {result.isPartial && <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 4 }}>Partial Payment · Remaining: ₹{(result.monthlyFee - result.amount).toLocaleString('en-IN')}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Ref: {result.ref}</div>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Link</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control" value={result.link} readOnly style={{ fontSize: 12 }} />
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(result.link)}><Copy size={13} /></button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp Message</label>
                <textarea className="form-control" rows={9} value={result.waMessage} readOnly style={{ fontSize: 12, lineHeight: 1.7 }} />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result ? (
            <button className="btn btn-primary" onClick={generate} disabled={loading}>
              {loading ? <span className="spinner" /> : belowFifty ? '⏳ Request Approval' : '🔗 Generate Link'}
            </button>
          ) : (
            <button className="btn btn-whatsapp" onClick={() => { window.open(result.waLink, '_blank'); toast.success('WhatsApp opened!'); }}>
              <MessageCircle size={14} /> Send via WhatsApp
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
